import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { animated } from "@react-spring/web";
import { useSearchParams } from "react-router-dom";
import { Question } from "../../models/question.js";
import BgTexture from "../../assets/images/background-texture.png";
import Blob from "./Blob.jsx";
import Speaker from "./Speaker.jsx";
import DisplayQuestion from "./DisplayQuestion.jsx";
import * as service from "./DataViz.service.js";
import Chart from "./Chart.jsx";
import enStrings from "../../services/strings.en.json";
import enDIYStrings from "../../services/strings.en.diy.json";
import esDIYStrings from "../../services/strings.es.diy.json";
import { getString } from "../../services/stringsHelpers.js";

/**
 * @typedef {Object} ChartDatum
 * @property {Object} custom
 * @property {Object} custom.labelPosition
 * @property {number} custom.labelPosition.x
 * @property {number} custom.labelPosition.y
 * @property {string} custom.connectorSide
 * @property {number} val
 * @property {string} name
 * @property {import("../../models/dataVizConfig.js").DataVizConfig} dataViz
 */

/**
 * @component
 * @param {Object} args
 * @param {Question} args.topicConfig
 * @param {React.MutableRefObject<(() => Promise<boolean[]>) | undefined>} args.animationFnRef
 * @param {number} args.intervalDuration
 */
function ChartSlide({ topicConfig, animationFnRef, intervalDuration }) {
  const {
    dataViz: {
      images: {
        persona: { orientation, src },
      },
      style,
    },
  } = topicConfig;

  const [params] = useSearchParams();
  const isDIY = params.get("diy");
  const primary =
    topicConfig.getTopic(isDIY ? enDIYStrings : enStrings)?.color.primary ||
    "#D12287";

  const { styles, resets, springRefs } = service.useAnimations(topicConfig);
  const [chartData, setChartData] = useState(/** @type {ChartDatum[]} */ ([]));
  const containerRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  service.useAnnounceSlide({
    chartData,
    itemConfig: topicConfig,
    strings: isDIY ? enDIYStrings : enStrings,
  });

  useEffect(() => {
    service
      .getChartData({ itemConfig: topicConfig })
      .then((res) => setChartData(res));
    springRefs.displayQuestionEnSpringRef.start();
    springRefs.speakerSpringRef.start();
    springRefs.blobSpringRef.start();
    springRefs.chartSpringRef.start();
    if (isDIY) {
      setTimeout(() => {
        resets.displayQuestionEn().then(() => {
          resets.chart().then(() => {
            setChartData((prev) =>
              prev.map((ea) => ({
                ...ea,
                name: getString(ea.name, esDIYStrings),
              }))
            );
            springRefs.chartSpringRef.start();
            springRefs.displayQuestionEsSpringRef.start();
          });
        });
      }, intervalDuration / 2);
    }
    // eslint-disable-next-line no-param-reassign
    animationFnRef.current = () => {
      const promises = [resets.blob(), resets.speaker(), resets.chart()];
      if (isDIY) {
        promises.push(resets.displayQuestionEs());
      } else {
        promises.push(resets.displayQuestionEn());
      }
      return Promise.all(promises);
    };
  }, [topicConfig]);

  return (
    <div className="dataviz__chart__container" ref={containerRef}>
      <img
        src={BgTexture}
        className={`dataviz__bg${
          orientation ? ` dataviz__bg--${orientation}` : ""
        }`}
        alt=""
        style={style?.background}
      />
      <Blob
        color={primary}
        orientation={orientation}
        springs={styles.blob}
        style={style?.blob}
      />
      <Speaker
        persona={topicConfig.persona}
        imageSrc={src}
        orientation={orientation}
        springs={styles.speaker}
        style={style?.persona}
      />
      <DisplayQuestion
        text={topicConfig.shortText}
        question={topicConfig}
        orientation={orientation}
        springs={styles.displayQuestionEn}
        style={style?.text}
        containerRef={containerRef}
      />
      {isDIY ? (
        <DisplayQuestion
          text={getString(topicConfig.shortText, esDIYStrings)}
          question={topicConfig}
          orientation={orientation}
          springs={styles.displayQuestionEs}
          style={style?.text}
          containerRef={containerRef}
        />
      ) : null}
      {chartData.length ? (
        <Chart
          chartId={topicConfig.id}
          chartType={topicConfig.dataViz.chartType}
          data={chartData}
          orientation={orientation}
          springs={styles.chart}
          style={style?.chart}
        />
      ) : (
        <animated.p
          className={`dataviz__no-data-copy${
            orientation ? ` dataviz__no-data-copy--${orientation}` : ""
          }`}
          style={styles.chart}
        >
          {service.getNoResponseCopy(isDIY ? enDIYStrings : enStrings)}
        </animated.p>
      )}
    </div>
  );
}

ChartSlide.propTypes = {
  topicConfig: PropTypes.instanceOf(Question).isRequired,
  animationFnRef: PropTypes.shape({ current: PropTypes.func }).isRequired,
  intervalDuration: PropTypes.number.isRequired,
};

export default ChartSlide;
